import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PasswordInputType } from 'common/constant/PasswordInputType';
import { PasswordToggle } from 'common/constant/PasswordToggle';
import { RoutePath } from 'common/constant/route-path';
import { Logger } from 'common/utility/logger/logger';
import { catchError, Observable, of, switchMap, take } from 'rxjs';
import { CognitoService } from 'services/authentication/cognito/cognito.service';
import { PoolResult } from 'services/authentication/cognito/model/pool-result';

const controls = {
  organizationId: 'organizationId',
  username: 'username',
  verificationCode: 'verificationCode',
  newPassword: 'newPassword',
  repeatPassword: 'repeatPassword',
};

@Component({
  selector: 'ems-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent implements OnInit {
  private passwordToggles: Array<boolean> = new Array(3).fill(false)

  private requested: boolean = false
  private reset: boolean = false

  private poolId?: string
  private clientId?: string

  private errors: Array<string> = []

  public form: FormGroup = this.createForm();

  public get visiblePasswords(): boolean {
    return this.requested
  }

  public get organizationId(): FormControl {
    return this.getFormControl(controls.organizationId);
  }

  public get username(): FormControl {
    return this.getFormControl(controls.username);
  }

  public get verificationCode(): FormControl {
    return this.getFormControl(controls.verificationCode)
  }

  public get newPassword(): FormControl {
    return this.getFormControl(controls.newPassword)
  }

  public get repeatPassword(): FormControl {
    return this.getFormControl(controls.repeatPassword)
  }

  public get validCredentials(): boolean {
    return this.username.valid && this.organizationId.valid && this.passwordsValid && this.verificationCodeValid
  }

  public get invalidCredentials(): boolean {
    return !this.validCredentials;
  }

  public get newLabel(): string {
    return this.getLabel('new')
  }

  public get cancelButtonLabel(): string {
    return `global.button.${this.reset ? "return" : "cancel"}`
  }

  public get verificationLabel(): string {
    return this.getLabel('verification')
  }

  public get repeatLabel(): string {
    return this.getLabel('repeat')
  }

  public SubmitClicked(): void {
    this.submitRequest()
  }

  public get ButtonDisabled(): boolean {
    return this.invalidCredentials || this.reset
  }

  public get passwordToggle(): typeof PasswordToggle {
    return PasswordToggle
  }

  public get Errors(): Array<string> {
    return this.errors
  }

  public get AnyErrors(): boolean {
    return !!this.Errors.length
  }

  public get DefaultError(): string {
    return this.getError('default')
  }


  public CancelClicked(): void {
    this.returnToLogin()
  }

  public passwordVisible(identifier: PasswordToggle): boolean {
    return this.passwordToggles[identifier]
  }

  public togglePasswordVisibility(identifier: PasswordToggle): void {
    this.passwordToggles[identifier] = !this.passwordToggles[identifier]
  }

  public passwordInputType(identifier: PasswordToggle): string {
    return this.passwordVisible(identifier) ? PasswordInputType.ClearText : PasswordInputType.MaskedInput
  }

  constructor(
    private formBuilder: FormBuilder,
    private cognitoService: CognitoService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.poolId = cognitoService.PoolId
    this.clientId = cognitoService.ClientId
  }

  ngOnInit(): void {
    this.getQueryParameters()
  }

  private submitRequest(): void {
    if (!this.requested) {
      this.cognitoService.getPoolInfo(this.organizationId.value)
        .pipe(
          catchError(error => {
            Logger.error(`Unable to get password verification code for user '${this.username.value}' in organization: '${this.organizationId.value}' :  ${error}`)
            this.errors.push(this.getError('forgot-verify-code'))
            return of(null);
          }),
          switchMap((poolInfo: PoolResult) => {
            this.poolId = poolInfo.poolId
            this.clientId = poolInfo.clientId
            return this.cognitoService.poolIdPasswordVerify(this.username.value, poolInfo.poolId, poolInfo.clientId)
          })
        )
        .subscribe((result: any) => {
          this.requested = !!result
        })

    } else {
      // When routed from an email link, the above step wasn't executed so the poolId is not set
      if (!this.poolId || !this.clientId) {
        this.cognitoService.getPoolInfo(this.organizationId.value)
          .pipe(
            catchError(error => {
              Logger.error(`Unable to get password verification code for user '${this.username.value}' in organization: '${this.organizationId.value}' :  ${error}`)
              this.errors.push(this.getError('forgot-verify-code'))
              return of(null);
            }),
            switchMap((poolInfo: PoolResult) => {
              return !!poolInfo ? this.forgotPasswordSubmit(poolInfo?.poolId, poolInfo?.clientId) : of(null)
            }),
            take(1)
          )
          .subscribe((result: any) => {
            this.processSubmission(result)
          })

      }
      else {
        this.forgotPasswordSubmit(this.poolId, this.clientId)
          .pipe(take(1))
          .subscribe((result: any) => {
            this.processSubmission(result)
          })
      }
    }
  }

  private forgotPasswordSubmit(poolId?: string, clientId?: string): Observable<any> {
    return this.cognitoService.forgotPasswordSubmit(this.username.value, this.verificationCode.value, this.newPassword.value, poolId, clientId)
  }

  private processSubmission(result: any): void {
    this.reset = result === "SUCCESS"
    if (this.reset) this.returnToLogin()
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      organizationId: [null, [Validators.required]],
      username: [null, [Validators.required]],
      verificationCode: [null, [Validators.required]],
      newPassword: [null, [Validators.required]],
      repeatPassword: [null, [Validators.required]]
    });
  }

  private getFormControl(controlName: string): FormControl {
    return this.form.controls[controlName] as FormControl;
  }

  private getLabel(key: string): string {
    return `password.${key}`
  }

  private get passwordsValid(): boolean {
    return !this.requested || (this.newPassword.valid && this.repeatPassword.valid && this.passwordsMatch)
  }

  private get verificationCodeValid(): boolean {
    return !this.requested || this.verificationCode.valid
  }

  private get passwordsMatch(): boolean {
    return this.newPassword.value === this.repeatPassword.value
  }

  private returnToLogin(): void {
    this.router.navigate([RoutePath.Login])
  }

  private getQueryParameters(): void {
    this.route.queryParams.subscribe(params => {
      if (!!params['requested']) {
        this.requested = true;
      }
    });
  }

  private getError(key: string): string {
    return `password.errors.${key}`
  }


}
